import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Send, Mail, RefreshCw, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LABEL = { drift: 'Rate-card drift digest', fx: 'FX movement digest', security: 'Weekly security digest' };

/** Shows the drift / FX digest exactly as it would be emailed, with recipients, before sending. */
export function DigestPreviewDialog({ kind, open, onClose, onSend, sending }) {
  const [data, setData] = useState(null);
  const [repricing, setRepricing] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  const { user } = useAuth();
  const canBulkReprice = ['cro', 'cco', 'super_admin'].includes(user?.role);
  const load = () => axios.get(`${API_URL}/api/insights/margin/digest-preview/${kind}`, { withCredentials: true })
    .then((r) => setData(r.data)).catch((e) => setData({ error: e?.response?.data?.detail || 'Could not load preview' }));
  useEffect(() => {
    if (!open || !kind) return;
    setData(null);
    load();
  }, [open, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const stillRows = data?.still_unrepriced_rows || [];
  const repriceable = stillRows.filter((r) => !r.locked);
  const bulkReprice = async () => {
    setRepricing(true);
    try {
      const r = await axios.post(`${API_URL}/api/insights/margin/reprice-unrepriced`, { ids: repriceable.map((x) => x.id) }, { withCredentials: true });
      if (r.data.requires_approval) { toast.info(r.data.message); setPendingApproval(r.data); } else { toast.success(r.data.message); await load(); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk reprice failed'); } finally { setRepricing(false); }
  };
  const html = data?.html ? `<html><body style="font-family:Roboto,Arial,sans-serif;color:#0F2040;font-size:13px;margin:12px">${data.html}</body></html>` : '';
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !sending) onClose(); }}>
      <DialogContent className="sm:max-w-[640px]" data-testid="digest-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040] flex items-center gap-2"><Mail className="w-4 h-4 text-[#4ABFCF]" /> Preview — {LABEL[kind] || 'digest'}</DialogTitle>
          <DialogDescription>{data?.subject ? <>Subject: <b className="text-[#0F2040]" data-testid="digest-preview-subject">{data.subject}</b></> : 'Rendered exactly as leadership would receive it.'}</DialogDescription>
        </DialogHeader>
        {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {data?.error && <p className="text-xs text-rose-600" data-testid="digest-preview-error">{data.error}</p>}
        {data && !data.error && (
          <div className="space-y-2">
            <iframe title="Digest preview" srcDoc={html} sandbox="" className="w-full h-64 rounded border border-slate-200 bg-white" data-testid="digest-preview-frame" />
            <div className="text-[11px] text-slate-600" data-testid="digest-preview-recipients">
              <span className="font-semibold text-[#0F2040]">{data.recipients?.length || 0} recipient(s):</span> {(data.recipients || []).join(', ') || '—'}
            </div>
            <p className={`text-[11px] ${data.would_send ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="digest-preview-status">
              {data.would_send ? `${data.count} item(s) — ready to send.` : `Would not send — ${data.reason}.`}
            </p>
            {kind === 'drift' && stillRows.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 space-y-1.5" data-testid="digest-bulk-reprice">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] text-amber-800">
                    <b>{stillRows.length}</b> deal(s) still carry pre-change rates{stillRows.length - repriceable.length > 0 ? ` · ${stillRows.length - repriceable.length} locked (skipped)` : ''}.
                  </p>
                  {canBulkReprice ? (
                    <Button size="sm" onClick={bulkReprice} disabled={repricing || repriceable.length === 0} className="h-7 text-xs bg-[#0F2040] hover:bg-[#1a2f5a] text-white" data-testid="digest-bulk-reprice-btn">
                      {repricing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" /> Reprice {repriceable.length} now</>}
                    </Button>
                  ) : <span className="text-[10px] text-slate-500" data-testid="digest-bulk-reprice-no-permission">CRO/CCO/Super Admin can reprice</span>}
                </div>
                {pendingApproval && (
                  <p className="text-[10px] text-amber-800 font-medium" data-testid="digest-bulk-reprice-pending">
                    Awaiting a second leader: A${Math.round(pendingApproval.swing_aud).toLocaleString()} swing is above the A${Math.round(pendingApproval.threshold_aud).toLocaleString()} approval threshold · {pendingApproval.approvers_notified} leader(s) notified.
                  </p>
                )}
                <ul className="text-[10px] text-slate-600 space-y-0.5 max-h-24 overflow-y-auto">
                  {stillRows.map((r) => (
                    <li key={r.id} className="flex items-center gap-1" data-testid={`digest-still-row-${r.id}`}>
                      {r.locked && <Lock className="w-2.5 h-2.5 text-slate-400" />}<span className="font-medium text-[#0F2040] truncate">{r.name}</span>
                      <span className="text-slate-400">· {r.move_pct > 0 ? '+' : ''}{r.move_pct}% · {r.rate_changes?.[0]}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="digest-preview-close-btn">Close</Button>
          {onSend && (
            <Button onClick={onSend} disabled={sending || !data?.would_send} className="bg-[#0F2040] hover:bg-[#1a2f5a] text-white" data-testid="digest-preview-send-btn">
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Send now</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
